import React, { useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import MaintenanceRecordDetail from "../../components/maintenance/MaintenanceRecordDetail";
import {
  fetchMaintenanceRecord,
  fetchMaintenanceRecordSuccess,
  fetchMaintenanceRecordFailure,
  startMaintenanceRecord,
  startMaintenanceRecordSuccess,
  startMaintenanceRecordFailure,
  completeMaintenanceRecord,
  completeMaintenanceRecordSuccess,
  completeMaintenanceRecordFailure,
  cancelMaintenanceRecord,
  cancelMaintenanceRecordSuccess,
  cancelMaintenanceRecordFailure,
  uploadMaintenancePhoto,
  uploadMaintenancePhotoSuccess,
  uploadMaintenancePhotoFailure,
  deleteMaintenancePhoto,
  deleteMaintenancePhotoSuccess,
  deleteMaintenancePhotoFailure,
  clearCurrentRecord,
} from "../../store/maintenance/actions";

const MaintenanceRecordDetailContainer = ({
  records,
  fetchMaintenanceRecord,
  startMaintenanceRecord,
  completeMaintenanceRecord,
  cancelMaintenanceRecord,
  uploadMaintenancePhoto,
  deleteMaintenancePhoto,
  clearCurrentRecord,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchMaintenanceRecord(id);
    }
    return () => {
      clearCurrentRecord();
    };
  }, [id, fetchMaintenanceRecord, clearCurrentRecord]);

  const handleStart = useCallback(
    (recordId) => {
      startMaintenanceRecord(recordId, () => {
        fetchMaintenanceRecord(recordId);
      });
    },
    [startMaintenanceRecord, fetchMaintenanceRecord]
  );

  const handleComplete = useCallback(
    (recordId, data) => {
      completeMaintenanceRecord(recordId, data, () => {
        fetchMaintenanceRecord(recordId);
      });
    },
    [completeMaintenanceRecord, fetchMaintenanceRecord]
  );

  const handleCancel = useCallback(
    (recordId) => {
      if (window.confirm("Are you sure you want to cancel this maintenance?")) {
        cancelMaintenanceRecord(recordId, () => {
          navigate("/maintenance/records");
        });
      }
    },
    [cancelMaintenanceRecord, navigate]
  );

  const handleAddPhoto = useCallback(
    (photoData) => {
      uploadMaintenancePhoto(photoData, () => {
        fetchMaintenanceRecord(id);
      });
    },
    [uploadMaintenancePhoto, fetchMaintenanceRecord, id]
  );

  const handleDeletePhoto = useCallback(
    (photoId) => {
      if (window.confirm("Delete this photo?")) {
        deleteMaintenancePhoto(photoId, () => {
          fetchMaintenanceRecord(id);
        });
      }
    },
    [deleteMaintenancePhoto, fetchMaintenanceRecord, id]
  );

  return (
    <MaintenanceRecordDetail
      record={records.current}
      loading={records.loading}
      error={records.error}
      onStart={handleStart}
      onComplete={handleComplete}
      onCancel={handleCancel}
      onAddPhoto={handleAddPhoto}
      onDeletePhoto={handleDeletePhoto}
    />
  );
};

const mapStateToProps = (state) => ({
  records: state.maintenance.records,
});

const mapDispatchToProps = (dispatch) => ({
  fetchMaintenanceRecord: (id) => {
    dispatch(fetchMaintenanceRecord(id)).payload.then(
      (response) => {
        dispatch(fetchMaintenanceRecordSuccess(response.data));
      },
      (error) => {
        dispatch(fetchMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  startMaintenanceRecord: (id, callback) => {
    dispatch(startMaintenanceRecord(id)).payload.then(
      (response) => {
        dispatch(startMaintenanceRecordSuccess(response.data));
        if (callback) callback();
      },
      (error) => {
        dispatch(startMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  completeMaintenanceRecord: (id, data, callback) => {
    dispatch(completeMaintenanceRecord(id, data)).payload.then(
      (response) => {
        dispatch(completeMaintenanceRecordSuccess(response.data));
        if (callback) callback();
      },
      (error) => {
        dispatch(completeMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  cancelMaintenanceRecord: (id, callback) => {
    dispatch(cancelMaintenanceRecord(id)).payload.then(
      (response) => {
        dispatch(cancelMaintenanceRecordSuccess(response.data));
        if (callback) callback();
      },
      (error) => {
        dispatch(cancelMaintenanceRecordFailure(error.response?.data || error));
      }
    );
  },
  uploadMaintenancePhoto: (photoData, callback) => {
    dispatch(uploadMaintenancePhoto(photoData)).payload.then(
      (response) => {
        dispatch(uploadMaintenancePhotoSuccess(response.data));
        if (callback) callback();
      },
      (error) => {
        dispatch(uploadMaintenancePhotoFailure(error.response?.data || error));
      }
    );
  },
  deleteMaintenancePhoto: (id, callback) => {
    dispatch(deleteMaintenancePhoto(id)).payload.then(
      () => {
        dispatch(deleteMaintenancePhotoSuccess(id));
        if (callback) callback();
      },
      (error) => {
        dispatch(deleteMaintenancePhotoFailure(error.response?.data || error));
      }
    );
  },
  clearCurrentRecord: () => {
    dispatch(clearCurrentRecord());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceRecordDetailContainer);
